import React, { useEffect, useState, useCallback } from "react";
import { AdminShell } from "./AdminShell";
import { adminApi } from "./adminApi";
import { Wallet, TrendingUp, Users, Scale, Plus, Trash2, Loader2, Check, Calculator, History, PiggyBank } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(Number(n || 0));
const thisMonth = () => new Date().toISOString().slice(0, 7);

const Card = ({ title, value, icon: Icon, accent, sub }) => (
  <div className="bg-[#111114] border border-white/10 rounded-[5px] p-4 flex flex-col gap-1.5" data-testid={`fin-kpi-${(title || "").toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-zinc-500">
      {Icon && <Icon className="w-3.5 h-3.5 text-[#C19A6B]" />} {title}
    </div>
    <div className={`text-xl font-light ${accent || "text-white"}`}>{value}</div>
    {sub && <div className="text-[11px] text-zinc-500">{sub}</div>}
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <h2 className="flex items-center gap-2 text-sm uppercase tracking-wider text-[#C19A6B] mb-4 mt-10">
    <Icon className="w-4 h-4" /> {children}
  </h2>
);

const inputCls = "bg-white/5 border border-white/10 rounded-[5px] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C19A6B] w-full";

export default function AdminFinance({ embedded }) {
  const [ov, setOv] = useState(null);
  const [inv, setInv] = useState({ investments: [], totals: { vincent: 0, seb: 0 } });
  const [expenses, setExpenses] = useState([]);
  const [dists, setDists] = useState([]);
  const [cfgPct, setCfgPct] = useState(15);
  const [cfgSaved, setCfgSaved] = useState(false);

  // forms
  const [invForm, setInvForm] = useState({ partner: "vincent", amount: "", date: "", note: "" });
  const [invBusy, setInvBusy] = useState(false);
  const [expForm, setExpForm] = useState({ month: thisMonth(), publication: "", operation: "", other: "", note: "" });
  const [expBusy, setExpBusy] = useState(false);

  // closing
  const [selMonth, setSelMonth] = useState(thisMonth());
  const [preview, setPreview] = useState(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeComment, setCloseComment] = useState("");

  const loadAll = useCallback(async () => {
    const [o, i, e, d, c] = await Promise.all([
      adminApi.finOverview(), adminApi.finInvestments(), adminApi.finExpenses(), adminApi.finDistributions(), adminApi.finConfig(),
    ]);
    setOv(o); setInv(i); setExpenses(e.expenses || []); setDists(d.distributions || []); setCfgPct(c.remun_pct);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadPreview = useCallback(async (m) => {
    if (!m) return;
    const p = await adminApi.finMonth(m);
    setPreview(p);
    setCloseComment((p.distribution && p.distribution.comment) || "");
  }, []);
  useEffect(() => { loadPreview(selMonth); }, [selMonth, loadPreview]);

  const saveConfig = async () => {
    await adminApi.finSaveConfig({ remun_pct: Number(cfgPct) });
    setCfgSaved(true); setTimeout(() => setCfgSaved(false), 2000);
    loadAll(); loadPreview(selMonth);
  };

  const addInvestment = async () => {
    if (!invForm.amount || Number(invForm.amount) <= 0) return;
    setInvBusy(true);
    try {
      await adminApi.finAddInvestment({ ...invForm, amount: Number(invForm.amount) });
      setInvForm({ partner: "vincent", amount: "", date: "", note: "" });
      await loadAll(); await loadPreview(selMonth);
    } finally { setInvBusy(false); }
  };
  const delInvestment = async (id) => { await adminApi.finDelInvestment(id); await loadAll(); await loadPreview(selMonth); };

  const saveExpense = async () => {
    if (!expForm.month) return;
    setExpBusy(true);
    try {
      await adminApi.finSaveExpense({
        month: expForm.month,
        publication: Number(expForm.publication || 0),
        operation: Number(expForm.operation || 0),
        other: Number(expForm.other || 0),
        note: expForm.note,
      });
      await loadAll(); await loadPreview(selMonth); await refreshExpenses();
    } finally { setExpBusy(false); }
  };
  const refreshExpenses = async () => { const e = await adminApi.finExpenses(); setExpenses(e.expenses || []); };
  const editExpense = (x) => setExpForm({ month: x.month, publication: x.publication, operation: x.operation, other: x.other, note: x.note || "" });

  const closeMonth = async () => {
    setCloseBusy(true);
    try {
      await adminApi.finClose({ month: selMonth, comment: closeComment });
      await loadAll(); await loadPreview(selMonth);
    } finally { setCloseBusy(false); }
  };
  const delDist = async (id) => { await adminApi.finDelDistribution(id); await loadAll(); await loadPreview(selMonth); };

  const p = preview && preview.preview;

  return (
    <AdminShell embedded={embedded} title="Finances & associés">
      {!ov ? (
        <p className="text-zinc-500 text-sm">Chargement…</p>
      ) : (
        <div data-testid="admin-finance">
          {/* ================= TABLEAU DE BORD ================= */}
          <SectionTitle icon={TrendingUp}>Tableau de bord des associés</SectionTitle>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card title="Chiffre d'affaires cumulé" value={eur(ov.revenue_cum)} icon={Wallet} sub="Auto — paiements Stripe" />
            <Card title="Total des frais" value={eur(ov.expenses_cum)} icon={Calculator} />
            <Card title="Bénéfice net cumulé" value={eur(ov.net_profit_cum)} icon={TrendingUp} accent={ov.net_profit_cum >= 0 ? "text-emerald-400" : "text-red-400"} />
            <Card title="Part société restante" value={eur(ov.company_retains_cum)} icon={PiggyBank} />
            <Card title="Investi — Vincent" value={eur(ov.invested.vincent)} icon={Users} />
            <Card title="Investi — Seb" value={eur(ov.invested.seb)} icon={Users} />
            <Card title="Écart d'investissement" value={eur(ov.invest_gap)} icon={Scale} sub={ov.invest_gap > 0 ? `En faveur de ${ov.invest_over === "vincent" ? "Vincent" : "Seb"}` : "Équilibré"} accent="text-[#C19A6B]" />
            <Card title="Compensation restante" value={eur(ov.compensation_remaining)} icon={Scale} sub={`Déjà compensé : ${eur(ov.compensation_paid_total)}`} />
            <Card title="Rému versée — Vincent" value={eur(ov.remun_paid_vincent)} icon={Wallet} sub={`Solde estimé : ${eur(ov.solde_remaining_vincent)}`} />
            <Card title="Rému versée — Seb" value={eur(ov.remun_paid_seb)} icon={Wallet} sub={`Solde estimé : ${eur(ov.solde_remaining_seb)}`} />
            <Card title="Versé total — Vincent" value={eur(ov.paid_total_vincent)} icon={Wallet} accent="text-[#C19A6B]" />
            <Card title="Versé total — Seb" value={eur(ov.paid_total_seb)} icon={Wallet} accent="text-[#C19A6B]" />
          </div>

          {/* Config % rémunération */}
          <div className="bg-[#111114] border border-white/10 rounded-[5px] p-4 mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="text-sm text-zinc-300">Rémunération de chaque associé (% du bénéfice net)</span>
            <input type="number" min="0" max="50" step="0.5" value={cfgPct} onChange={(e) => setCfgPct(e.target.value)} data-testid="fin-cfg-pct" className="bg-white/5 border border-white/10 rounded-[5px] px-3 py-2 text-sm text-white w-28 focus:outline-none focus:border-[#C19A6B]" />
            <button onClick={saveConfig} data-testid="fin-cfg-save" className="bg-[#C19A6B] hover:bg-[#d1af84] text-white text-xs font-medium uppercase tracking-wider px-4 py-2 rounded-[5px] transition-colors flex items-center gap-2">
              {cfgSaved ? <><Check className="w-4 h-4" /> Enregistré</> : "Enregistrer"}
            </button>
          </div>

          {/* ================= CLÔTURE MENSUELLE ================= */}
          <SectionTitle icon={Calculator}>Clôture mensuelle & répartition</SectionTitle>
          <div className="bg-[#111114] border border-white/10 rounded-[5px] p-4">
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Mois</label>
                <input type="month" value={selMonth} onChange={(e) => setSelMonth(e.target.value)} data-testid="fin-close-month" className="bg-white/5 border border-white/10 rounded-[5px] px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C19A6B]" />
              </div>
            </div>
            {p && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="fin-preview-table">
                  <tbody className="divide-y divide-white/5">
                    <tr><td className="py-2 text-zinc-400">Chiffre d'affaires du mois <span className="text-[10px] uppercase text-[#C19A6B]">(auto Stripe)</span></td><td className="py-2 text-right text-white">{eur(p.revenue)}</td></tr>
                    <tr><td className="py-2 text-zinc-400">Total des frais</td><td className="py-2 text-right text-white">− {eur(p.total_expenses)}</td></tr>
                    <tr><td className="py-2 text-zinc-300 font-medium">Bénéfice net</td><td className={`py-2 text-right font-medium ${p.net_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{eur(p.net_profit)}</td></tr>
                    <tr><td className="py-2 text-zinc-400">Rémunération Vincent ({p.remun_pct}%)</td><td className="py-2 text-right text-white">{eur(p.remun_vincent)}</td></tr>
                    <tr><td className="py-2 text-zinc-400">Rémunération Seb ({p.remun_pct}%)</td><td className="py-2 text-right text-white">{eur(p.remun_seb)}</td></tr>
                    <tr><td className="py-2 text-zinc-400">Bénéfice restant après rémunérations</td><td className="py-2 text-right text-white">{eur(p.profit_after_remun)}</td></tr>
                    <tr><td className="py-2 text-zinc-400">Compensation d'investissement {p.compensation_to ? `→ ${p.compensation_to === "vincent" ? "Vincent" : "Seb"}` : ""}</td><td className="py-2 text-right text-[#C19A6B]">{eur(p.compensation_amount)}</td></tr>
                    <tr className="bg-white/[0.02]"><td className="py-2 text-white font-medium">Versé à Vincent</td><td className="py-2 text-right text-[#C19A6B] font-medium">{eur(p.paid_vincent)}</td></tr>
                    <tr className="bg-white/[0.02]"><td className="py-2 text-white font-medium">Versé à Seb</td><td className="py-2 text-right text-[#C19A6B] font-medium">{eur(p.paid_seb)}</td></tr>
                    <tr><td className="py-2 text-zinc-400">Conservé par la société</td><td className="py-2 text-right text-white">{eur(p.company_retains)}</td></tr>
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Commentaire</label>
                <input value={closeComment} onChange={(e) => setCloseComment(e.target.value)} data-testid="fin-close-comment" className={inputCls} placeholder="Note sur cette clôture…" />
              </div>
              <button onClick={closeMonth} disabled={closeBusy} data-testid="fin-close-btn" className="bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white text-xs font-medium uppercase tracking-wider px-5 py-2.5 rounded-[5px] transition-colors flex items-center justify-center gap-2">
                {closeBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                {preview && preview.distribution ? "Mettre à jour la clôture" : "Clôturer le mois"}
              </button>
            </div>
            {preview && preview.distribution && <p className="text-[11px] text-emerald-400 mt-2">Ce mois est déjà clôturé — vous pouvez recalculer et mettre à jour.</p>}
          </div>

          {/* ================= INVESTISSEMENTS ================= */}
          <SectionTitle icon={Users}>Investissements des associés</SectionTitle>
          <div className="bg-[#111114] border border-white/10 rounded-[5px] p-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
              <select value={invForm.partner} onChange={(e) => setInvForm({ ...invForm, partner: e.target.value })} data-testid="fin-inv-partner" className={inputCls}>
                <option value="vincent" className="bg-[#0d0d10]">Vincent</option>
                <option value="seb" className="bg-[#0d0d10]">Seb</option>
              </select>
              <input type="number" min="0" step="0.01" placeholder="Montant €" value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} data-testid="fin-inv-amount" className={inputCls} />
              <input type="date" value={invForm.date} onChange={(e) => setInvForm({ ...invForm, date: e.target.value })} data-testid="fin-inv-date" className={inputCls} />
              <input placeholder="Note (optionnel)" value={invForm.note} onChange={(e) => setInvForm({ ...invForm, note: e.target.value })} data-testid="fin-inv-note" className={inputCls} />
            </div>
            <button onClick={addInvestment} disabled={invBusy} data-testid="fin-inv-add" className="mb-4 inline-flex items-center gap-2 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 text-xs font-medium uppercase tracking-wider px-4 py-2 rounded-[5px] transition-colors">
              {invBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter un investissement
            </button>
            <div className="divide-y divide-white/5 border-t border-white/10">
              {inv.investments.length === 0 ? (
                <p className="text-zinc-500 text-sm py-3">Aucun investissement enregistré.</p>
              ) : inv.investments.map((x) => (
                <div key={x.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`fin-inv-row-${x.id}`}>
                  <span className="w-20 text-[#C19A6B] capitalize">{x.partner}</span>
                  <span className="w-28 text-white">{eur(x.amount)}</span>
                  <span className="w-24 text-zinc-500">{x.date}</span>
                  <span className="flex-1 text-zinc-400 truncate">{x.note}</span>
                  <button onClick={() => delInvestment(x.id)} data-testid={`fin-inv-del-${x.id}`} className="text-zinc-500 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* ================= FRAIS MENSUELS ================= */}
          <SectionTitle icon={Calculator}>Frais d'exploitation (par mois)</SectionTitle>
          <div className="bg-[#111114] border border-white/10 rounded-[5px] p-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Mois</label>
                <input type="month" value={expForm.month} onChange={(e) => setExpForm({ ...expForm, month: e.target.value })} data-testid="fin-exp-month" className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Publication</label>
                <input type="number" min="0" step="0.01" value={expForm.publication} onChange={(e) => setExpForm({ ...expForm, publication: e.target.value })} data-testid="fin-exp-pub" className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Fonctionnement</label>
                <input type="number" min="0" step="0.01" value={expForm.operation} onChange={(e) => setExpForm({ ...expForm, operation: e.target.value })} data-testid="fin-exp-op" className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Autres</label>
                <input type="number" min="0" step="0.01" value={expForm.other} onChange={(e) => setExpForm({ ...expForm, other: e.target.value })} data-testid="fin-exp-other" className={inputCls} />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Note</label>
                <input value={expForm.note} onChange={(e) => setExpForm({ ...expForm, note: e.target.value })} data-testid="fin-exp-note" className={inputCls} />
              </div>
            </div>
            <button onClick={saveExpense} disabled={expBusy} data-testid="fin-exp-save" className="mb-4 inline-flex items-center gap-2 border border-[#C19A6B]/50 text-[#C19A6B] hover:bg-[#C19A6B]/10 text-xs font-medium uppercase tracking-wider px-4 py-2 rounded-[5px] transition-colors">
              {expBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer les frais du mois
            </button>
            <div className="overflow-x-auto border-t border-white/10">
              <table className="w-full text-sm">
                <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left"><th className="py-2">Mois</th><th className="py-2 text-right">Publication</th><th className="py-2 text-right">Fonct.</th><th className="py-2 text-right">Autres</th><th className="py-2 text-right">Total</th><th className="py-2"></th></tr></thead>
                <tbody className="divide-y divide-white/5">
                  {expenses.length === 0 ? (
                    <tr><td colSpan="6" className="py-3 text-zinc-500">Aucun frais enregistré.</td></tr>
                  ) : expenses.map((x) => (
                    <tr key={x.month} data-testid={`fin-exp-row-${x.month}`}>
                      <td className="py-2 text-white">{x.month}</td>
                      <td className="py-2 text-right text-zinc-300">{eur(x.publication)}</td>
                      <td className="py-2 text-right text-zinc-300">{eur(x.operation)}</td>
                      <td className="py-2 text-right text-zinc-300">{eur(x.other)}</td>
                      <td className="py-2 text-right text-white">{eur((x.publication || 0) + (x.operation || 0) + (x.other || 0))}</td>
                      <td className="py-2 text-right"><button onClick={() => editExpense(x)} className="text-[11px] text-[#C19A6B] hover:underline">Modifier</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ================= HISTORIQUE ================= */}
          <SectionTitle icon={History}>Historique des distributions</SectionTitle>
          <div className="bg-[#111114] border border-white/10 rounded-[5px] p-4 overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap" data-testid="fin-dist-table">
              <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 text-left border-b border-white/10">
                <th className="py-2 pr-4">Mois</th><th className="py-2 pr-4 text-right">CA</th><th className="py-2 pr-4 text-right">Frais</th><th className="py-2 pr-4 text-right">Bénéf. net</th>
                <th className="py-2 pr-4 text-right">Rému V.</th><th className="py-2 pr-4 text-right">Rému S.</th><th className="py-2 pr-4 text-right">Compens.</th>
                <th className="py-2 pr-4 text-right">Versé V.</th><th className="py-2 pr-4 text-right">Versé S.</th><th className="py-2 pr-4">Commentaire</th><th className="py-2"></th>
              </tr></thead>
              <tbody className="divide-y divide-white/5">
                {dists.length === 0 ? (
                  <tr><td colSpan="11" className="py-3 text-zinc-500">Aucune distribution clôturée.</td></tr>
                ) : dists.map((d) => (
                  <tr key={d.id} data-testid={`fin-dist-row-${d.month}`}>
                    <td className="py-2 pr-4 text-white">{d.month}</td>
                    <td className="py-2 pr-4 text-right text-zinc-300">{eur(d.revenue)}</td>
                    <td className="py-2 pr-4 text-right text-zinc-300">{eur(d.total_expenses)}</td>
                    <td className={`py-2 pr-4 text-right ${d.net_profit >= 0 ? "text-emerald-400" : "text-red-400"}`}>{eur(d.net_profit)}</td>
                    <td className="py-2 pr-4 text-right text-zinc-300">{eur(d.remun_vincent)}</td>
                    <td className="py-2 pr-4 text-right text-zinc-300">{eur(d.remun_seb)}</td>
                    <td className="py-2 pr-4 text-right text-[#C19A6B]">{eur(d.compensation_amount)}{d.compensation_to ? ` (${d.compensation_to === "vincent" ? "V" : "S"})` : ""}</td>
                    <td className="py-2 pr-4 text-right text-white">{eur(d.paid_vincent)}</td>
                    <td className="py-2 pr-4 text-right text-white">{eur(d.paid_seb)}</td>
                    <td className="py-2 pr-4 text-zinc-400 max-w-[160px] truncate">{d.comment}</td>
                    <td className="py-2 text-right"><button onClick={() => delDist(d.id)} data-testid={`fin-dist-del-${d.month}`} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
